// MyEmployeeHome.jsx — Personal employee landing page for non-management roles.
// Shows: greeting + daily challenge + monthly courses + badges. Pulls from
// /training-advisor/* — no financial, sales, or operational data is exposed.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { useAuth } from "./lib/auth";
import { ksaHour } from "./lib/ksaTime";

function greeting() {
  const h = ksaHour();
  if (h < 5)  return "ليلة سعيدة";
  if (h < 12) return "صباح الخير";
  if (h < 17) return "مساء النور";
  if (h < 21) return "مساء الخير";
  return "أمسية طيبة";
}

export default function MyEmployeeHome({ onNavigate }) {
  const { user } = useAuth();
  const [data, setData] = useState({ courses: [], badges: [], challenge: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const empNo = user?.employee_no || user?.username;
    (async () => {
      const safe = async (p) => { try { return (await p).data; } catch { return null; } };
      const [c, b, ch] = await Promise.all([
        safe(api.get("/training-advisor/my-courses")),
        safe(api.get(`/training-advisor/badges/${empNo}`)),
        safe(api.get("/training-advisor/daily-challenge")),
      ]);
      setData({
        courses: c?.courses || [],
        badges: b?.badges || [],
        challenge: ch?.question ? ch : null,
        month: c?.month,
      });
      setLoading(false);
    })();
  }, [user]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>⏳ جاري تحضير صفحتك...</div>;
  }

  const recommended = data.courses.filter((c) => c.status === "recommended").length;
  const inProgress  = data.courses.filter((c) => c.status === "in_progress").length;
  const submitted   = data.courses.filter((c) => c.status === "submitted").length;
  const approved    = data.courses.filter((c) => c.status === "approved").length;

  return (
    <div data-testid="employee-home" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Hero */}
      <div style={{
        padding: "22px 26px", borderRadius: 14, marginBottom: 16,
        background: "linear-gradient(135deg, #6B4E3D 0%, #8B6F58 60%, #b08756 120%)",
        color: "#fff", boxShadow: "0 8px 24px rgba(107,78,61,.25)",
      }}>
        <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: 1 }}>MY DASHBOARD</div>
        <h2 style={{ margin: "4px 0 2px", fontSize: 24, fontWeight: 900 }}>
          👋 {greeting()}، {user.full_name || user.username}!
        </h2>
        <div style={{ fontSize: 12.5, opacity: 0.92 }}>
          هذي صفحتك الخاصة — كل تطورك ودوراتك وشاراتك في مكان واحد.
        </div>
      </div>

      {/* Quick stats */}
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginBottom: 14 }}>
        <Stat label="📚 دورات الشهر" value={data.courses.length} c="#7C3AED" testid="stat-courses-month" />
        <Stat label="⏳ قيد التنفيذ" value={inProgress} c="#0EA5E9" testid="stat-in-progress" />
        <Stat label="🕓 بانتظار اعتماد" value={submitted} c="#D97706" testid="stat-pending" />
        <Stat label="✅ معتمدة" value={approved} c="var(--success)" testid="stat-approved" />
        <Stat label="🏆 إنجازاتك" value={data.badges.length} c="#F59E0B" testid="stat-badges" />
      </div>

      {/* Daily Challenge teaser */}
      {data.challenge && !data.challenge.answered && (
        <div className="card" data-testid="home-challenge" style={{
          padding: 14, marginBottom: 14, cursor: "pointer",
          background: "linear-gradient(135deg, rgba(251,191,36,.15), rgba(255,255,255,.6))",
          borderInlineStart: "4px solid #F59E0B",
        }} onClick={() => onNavigate && onNavigate("training")}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#F59E0B", letterSpacing: 1 }}>🎯 تحدّي اليوم</div>
          <div style={{ fontWeight: 800, color: "var(--brown-deep)", marginTop: 4 }}>{data.challenge.question}</div>
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6 }}>اضغط هنا للإجابة وكسب شارة 🎯</div>
        </div>
      )}

      {/* Badges row */}
      {data.badges.length > 0 && (
        <div className="card" data-testid="home-badges" style={{
          padding: 14, marginBottom: 14,
          background: "linear-gradient(135deg, rgba(251,191,36,.10), rgba(255,255,255,.6))",
          borderInlineStart: "4px solid #F59E0B",
        }}>
          <h4 style={{ margin: "0 0 10px", color: "var(--brown-deep)", fontSize: 14 }}>🏆 سجل إنجازاتك ({data.badges.length})</h4>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {data.badges.map((b) => (
 <div key={b.id} 
                style={{ flex: "0 0 auto", padding: "10px 12px", background: "#fff", borderRadius: 10, border: "1px solid #F59E0B", minWidth: 130, textAlign: "center" }}>
                {/* تصحيح A3 (البند 1ج) — كان InfoTip=b.title يكرّر حرفياً العنوان
                    الظاهر أسفله مباشرة؛ حُذف. */}
                <div style={{ fontSize: 26, lineHeight: 1 }}>{b.icon}</div>
                <div style={{ fontWeight: 800, fontSize: 11, color: "var(--brown-deep)", marginTop: 4, lineHeight: 1.3, maxHeight: 28, overflow: "hidden" }}>{b.title}</div>
                {b.score != null && <div style={{ fontSize: 10, color: "#F59E0B", fontWeight: 700, marginTop: 2 }}>{b.score}/100</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Courses list (compact) */}
      <div className="card" style={{ padding: 14, marginBottom: 14, borderInlineStart: "4px solid #7C3AED" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h4 style={{ margin: 0, color: "var(--brown-deep)", fontSize: 14 }}>📚 دوراتك هذا الشهر ({data.month || ""})</h4>
          <button type="button" onClick={() => onNavigate && onNavigate("training")}
            data-testid="goto-training"
            style={{ padding: "5px 10px", background: "#7C3AED", color: "#fff", border: "none", borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            افتح صفحة التدريب →
          </button>
        </div>
        {data.courses.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
            🌱 لا توجد دورات بعد. سيقوم المستشار الذكي بتوصيتها لك تلقائياً قريباً.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {data.courses.slice(0, 5).map((c) => (
              <div key={c.id} style={{ padding: 8, background: "#FAFAF7", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{c.course_title}</div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{c.category}</div>
                </div>
                <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, fontWeight: 700,
                  background: c.status === "approved" ? "rgba(16,185,129,.15)"
                    : c.status === "submitted" ? "rgba(217,119,6,.15)"
                    : c.status === "in_progress" ? "rgba(14,165,233,.15)"
                    : "rgba(124,58,237,.15)",
                  color: c.status === "approved" ? "var(--success)"
                    : c.status === "submitted" ? "#D97706"
                    : c.status === "in_progress" ? "#0EA5E9" : "#7C3AED",
                }}>
                  {c.status === "approved" ? "✅ معتمدة" : c.status === "submitted" ? "🕓 بانتظار اعتماد"
                    : c.status === "in_progress" ? "⏳ قيد التنفيذ" : "📚 جديدة"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, c, testid }) {
  return (
    <div className="card" data-testid={testid} style={{ padding: 12, borderInlineStart: `3px solid ${c}` }}>
      <div style={{ fontSize: 11, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: c }}>{value}</div>
    </div>
  );
}
